import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Image, Input, Modal, Space, Switch, Tabs, message } from "antd";
import { EyeInvisibleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from "@emotion/styled";
import logo from '../assets/logo/logo.png'
import SpeechEntry from "../pages/SpeechEntry";
import { deactivateAccount, getLoginCaptcha, logout, CaptchaData, sendDeactivateEmailCode, sendDeactivatePhoneCode, getUserIdentity } from "../service/auth";
import { disableMinorMode, enableMinorMode } from '../service/minorMode';
import NumericPinInput from '../components/NumericPinInput';

const PrimaryLayout = (children: any) => {
  const login = useSelector((state: any) => state?.guard?.isLogin);
  const minorMode = useSelector((state: any) => state?.guard?.minorMode);
  const [isLogin, setIsLogin] = useState(!!Cookies.get('access_token'));
  const [modeDialog, setModeDialog] = useState<'enable' | 'disable' | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingMode, setSavingMode] = useState(false);
  const [pinVisible, setPinVisible] = useState(false);
  const [logoutModalShow, setLogoutModalShow] = useState(false);
  const [logoutVerifyType, setLogoutVerifyType] = useState<'password' | 'phone' | 'email'>('password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [logoutConfirmPassword, setLogoutConfirmPassword] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [userIdentity, setUserIdentity] = useState<{ phone: string | null; email: string | null }>({ phone: null, email: null });

  useEffect(() => {
    setIsLogin(!!Cookies.get('access_token'))
  }, [login])

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const data = await getLoginCaptcha();
      setCaptcha(data);
    } catch (error) {
      setCaptcha(null);
      console.error('获取图形验证码失败', error);
      messageApi.error(error instanceof Error ? error.message : '获取图形验证码失败');
    } finally {
      setCaptchaLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    if (codeCountdown > 0 || sendingCode) return;
    setSendingCode(true);
    try {
      if (logoutVerifyType === 'email') {
        await sendDeactivateEmailCode();
        setCodeCountdown(60);
        messageApi.success('验证码已发送至邮箱');
      } else if (logoutVerifyType === 'phone') {
        await sendDeactivatePhoneCode();
        setCodeCountdown(60);
        messageApi.success('验证码已发送至手机');
      }
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '发送验证码失败');
    } finally {
      setSendingCode(false);
    }
  };

  useEffect(() => {
    if (codeCountdown <= 0) return;
    const timer = window.setInterval(() => setCodeCountdown(value => value - 1), 1000);
    return () => window.clearInterval(timer);
  }, [codeCountdown]);

  useEffect(() => {
    if (logoutModalShow) {
      loadCaptcha();
      // 获取用户信息，判断 phone 和 email 是否可用
      getUserIdentity().then(identity => {
        setUserIdentity(identity);
        // 如果当前选中的 tab 对应的字段为 null，则切换到 password
        if (identity.phone === null && logoutVerifyType === 'phone') {
          setLogoutVerifyType('password');
        } else if (identity.email === null && logoutVerifyType === 'email') {
          setLogoutVerifyType('password');
        }
      }).catch(error => {
        console.error('获取用户信息失败', error);
        messageApi.error(error instanceof Error ? error.message : '获取用户信息失败');
      });
    } else {
      setCaptcha(null);
    }
  }, [logoutModalShow]);

  const confirmLogout = async () => {
    const errors: Record<string, boolean> = {
      captchaCode: !captchaCode.trim(),
    };
    if (logoutVerifyType === 'password') {
      errors.currentPassword = !currentPassword.trim();
      errors.logoutConfirmPassword = !logoutConfirmPassword.trim();
    } else if (logoutVerifyType === 'email') {
      errors.emailCode = !emailCode.trim();
    } else {
      errors.phoneCode = !phoneCode.trim();
    }

    setValidationErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      messageApi.warning('所有输入项均为必填项');
      return;
    }
    if (!captcha?.captchaId) {
      messageApi.error('图形验证码已失效，请刷新后重试');
      return;
    }
    if (logoutVerifyType === 'password' && currentPassword !== logoutConfirmPassword) {
      messageApi.warning('两次输入的密码不一致');
      setValidationErrors(previous => ({ ...previous, logoutConfirmPassword: true }));
      return;
    }

    const verification = logoutVerifyType === 'password'
      ? { verifyType: 'password' as const, password: currentPassword }
      : logoutVerifyType === 'phone'
        ? { verifyType: 'phone' as const, phoneCode: phoneCode.trim() }
        : { verifyType: 'email' as const, emailCode: emailCode.trim() };

    setDeactivating(true);
    try {
      await deactivateAccount(captcha.captchaId, captchaCode.trim(), verification);
      setLogoutModalShow(false);
      logout();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '账号注销失败');
      await loadCaptcha();
    } finally {
      setDeactivating(false);
    
    }
  };

  const clearValidationError = (field: string) => {
    setValidationErrors(previous => ({ ...previous, [field]: false }));
  };

  const items: any = [
    {
      label: '退出登录',
      key: 'logout'
    },
    {
      label: '注销',
      key: 'Exit'
    },
  ];

  // 点击
  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logout()
    }
    if (key === 'Exit') {
      setLogoutModalShow(true)
    }
  };

  const closeModeDialog = () => {
    setModeDialog(null);
    setPassword('');
    setConfirmPassword('');
    setPinVisible(false);
  };

  const saveMode = async () => {
    if (!/^\d{6}$/.test(password)) {
      messageApi.warning('请输入完整的 6 位数字密码');
      return;
    }
    if (modeDialog === 'enable' && password !== confirmPassword) {
      messageApi.warning('两次输入的密码不一致');
      return;
    }
    setSavingMode(true);
    try {
      if (modeDialog === 'enable') {
        await enableMinorMode(password);
        messageApi.success('未成年人模式已开启，声音采集功能已停用');
        closeModeDialog();
        return;
      }
      const verified = await disableMinorMode(password);
      if (!verified) {
        messageApi.error('密码不正确，请重新输入');
        return;
      }
      messageApi.success('未成年人模式已关闭');
      closeModeDialog();
    } catch {
      messageApi.error('未能保存未成年人模式设置，请确认当前页面使用 HTTPS 或 localhost 后重试');
    } finally {
      setSavingMode(false);
    }
  };

  return (
    <Frame>
      {contextHolder}
      <Header>
        <ImageWrap>
          <Image src={logo} preview={false} width={106} /><Line></Line> <Title>AI语音技术</Title>
        </ImageWrap>
        <div>
          {isLogin ? (
            <Space size={24}>
              <Space size={8}>
                <span>未成年人模式</span>
                <Switch
                  checked={minorMode}
                  onChange={() => setModeDialog(minorMode ? 'disable' : 'enable')}
                />
              </Space>
              <Dropdown menu={{ items, onClick }}>
                <AvatarDiv>
                  {Cookies.get('name') ? Cookies.get('name') + ' ' : ''}退出
                </AvatarDiv>
              </Dropdown>
            </Space>
          ) : null}
        </div>
      </Header>
      <Content>
        <SpeechEntry />
      </Content>
      <Footer>
        <span>深度合成算法备案：摩尔线程多模态大模型算法</span>
        <span>京公网安备11010502056110号</span>
        <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=11010502056110" target="_blank" rel="noreferrer">备案公示信息</a>
      </Footer>
      <Modal
        title={modeDialog === 'enable' ? '开启未成年人模式' : '关闭未成年人模式'}
        open={Boolean(modeDialog)}
        okText={modeDialog === 'enable' ? '设置密码并开启' : '验证密码并关闭'}
        cancelText="取消"
        confirmLoading={savingMode}
        okButtonProps={{ disabled: !/^\d{6}$/.test(password) || (modeDialog === 'enable' && !/^\d{6}$/.test(confirmPassword)) }}
        onOk={saveMode}
        onCancel={closeModeDialog}
        maskClosable={false}
        destroyOnClose
      >
        <p style={{ color: '#666', lineHeight: 1.7 }}>
          {modeDialog === 'enable'
            ? '开启后，语音识别、音色转换、声音复刻等声音录制和上传功能将不可使用。密码仅保存在当前浏览器中。'
            : '请输入开启未成年人模式时设置的密码。验证通过后才能关闭。'}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button
            type="text"
            size="small"
            icon={pinVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setPinVisible(visible => !visible)}
          >
            {pinVisible ? '隐藏密码' : '显示密码'}
          </Button>
        </div>
        <div style={{ marginBottom: modeDialog === 'enable' ? 18 : 0 }}>
          <div style={{ marginBottom: 8, color: '#444' }}>{modeDialog === 'enable' ? '设置 6 位数字密码' : '输入 6 位数字密码'}</div>
          <NumericPinInput
            autoFocus
            value={password}
            onChange={setPassword}
            label="未成年人模式密码"
            visible={pinVisible}
          />
        </div>
        {modeDialog === 'enable' && <div>
          <div style={{ marginBottom: 8, color: '#444' }}>再次输入确认</div>
          <NumericPinInput value={confirmPassword} onChange={setConfirmPassword} label="确认未成年人模式密码" visible={pinVisible} />
        </div>}
      </Modal>

      <Modal
        title="确认注销"
        open={logoutModalShow}
        footer={null}
        confirmLoading={deactivating}
        onCancel={() => setLogoutModalShow(false)}
        maskClosable={false}
      >
        <Tabs
          activeKey={logoutVerifyType}
          onChange={key => {
            setLogoutVerifyType(key as 'password' | 'phone' | 'email');
            setCaptchaCode('');
          }}
          items={[
            { key: 'password', label: '当前密码' },
            { key: 'phone', label: '手机验证码',disabled:userIdentity?.phone==null },
            { key: 'email', label: '邮箱验证码', disabled:userIdentity?.email==null},
          ]}
        />

        {logoutVerifyType === 'password' ? ( 
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Input.Password
              placeholder="当前密码"
              value={currentPassword}
              status={validationErrors.currentPassword ? 'error' : ''}
              onChange={event => { setCurrentPassword(event.target.value); clearValidationError('currentPassword'); }}
            />
            <Input.Password
              placeholder="再次确认密码"
              value={logoutConfirmPassword}
              status={validationErrors.logoutConfirmPassword ? 'error' : ''}
              onChange={event => { setLogoutConfirmPassword(event.target.value); clearValidationError('logoutConfirmPassword'); }}
            />
            <Input
              placeholder="请输入图形验证码"
              value={captchaCode}
              status={validationErrors.captchaCode ? 'error' : ''}
              onChange={event => { setCaptchaCode(event.target.value); clearValidationError('captchaCode'); }}
            />
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder={logoutVerifyType === 'email' ? '请输入邮箱验证码' : '请输入手机验证码'}
                value={logoutVerifyType === 'email' ? emailCode : phoneCode}
                status={validationErrors[logoutVerifyType === 'email' ? 'emailCode' : 'phoneCode'] ? 'error' : ''}
                onChange={event => {
                  if (logoutVerifyType === 'email') {
                    setEmailCode(event.target.value);
                    clearValidationError('emailCode');
                  } else {
                    setPhoneCode(event.target.value);
                    clearValidationError('phoneCode');
                  }
                }}
              />
              <Button onClick={sendVerificationCode} loading={sendingCode} disabled={codeCountdown > 0}>
                {codeCountdown > 0 ? `${codeCountdown}s 后重新发送` : `发送${logoutVerifyType === 'email' ? '邮箱' : '手机'}验证码`}
              </Button>
            </Space.Compact>
            <Input
              placeholder="请输入图形验证码"
              value={captchaCode}
              status={validationErrors.captchaCode ? 'error' : ''}
              onChange={event => { setCaptchaCode(event.target.value); clearValidationError('captchaCode'); }}
            />
          </Space>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          {captchaLoading ? (
            <span style={{ color: '#999' }}>验证码加载中...</span>
          ) : captcha ? (
            <img
              src={`data:image/png;base64,${captcha.imageBase64}`}
              alt="图形验证码"
              style={{ display: 'block', width: 140, height: 48, objectFit: 'contain', border: '1px solid #d9d9d9' }}
            />
          ) : (
            <span style={{ color: '#ff4d4f' }}>验证码加载失败</span>
          )}
          <Button type="link" icon={<ReloadOutlined />} onClick={loadCaptcha} loading={captchaLoading}>换一张</Button>
        </div>
        <Button
          type="primary"
          block
          style={{ marginTop: 20 }}
          onClick={confirmLogout}
          loading={deactivating}
        >
          确认注销账号
        </Button>
      </Modal>

    </Frame>
  );
}

export default PrimaryLayout;

const Frame = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const Header = styled.header`
  width: 100%;
  padding: 0 60px;
  height: 70px;
  background: #F8F8F8;
  display: flex;
  align-items: center;
  justify-content:space-between;
`
const ImageWrap = styled.div`
  height: 70px;
  background: #F8F8F8;
  display: flex;
  align-items: center;
  justify-content:space-between;
`

const Title = styled.span`
  font-size: 20px;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #777777;
  margin-left: 9.5px;
`

const Line = styled.span`
  width: 0px;
  height: 14px;
  opacity: 1;
  border: 0.5px solid #D8D8D8;
  margin-left: 9.5px;
`

const Content = styled.div`
  width: 100%;
  min-height: calc(100vh - 70px);
  background-color: #F8F8F8;
`
const AvatarDiv = styled.div`
`;

const Footer = styled.footer`
  width: 100%;
  padding: 18px 60px 24px;
  display: flex;
  justify-content: center;
  gap: 18px;
  color: #888;
  font-size: 12px;
  background: #f8f8f8;
  a { color: #888; }
`
